import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Flame,
  Droplets,
  Clock,
  Calendar,
  Award,
  ChevronRight,
  CheckCircle,
  Users,
  BookOpen
} from "lucide-react";
import InquiryForm from "@/components/InquiryForm";

const candleImage = "https://images.pexels.com/photos/3270223/pexels-photo-3270223.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";
const soapImage = "https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";

const specialCourses = [
  {
    id: "beginners_candle",
    title: "Beginners Candle Making Course",
    price: 45000,
    kitPrice: 15000,
    duration: "Contact for schedule",
    schedule: "Monday – Friday: 10:30–12:30 | 2:30–4:30",
    icon: Flame,
    image: candleImage,
    tagline: "Level 1 — Learn the basics of candle creation",
    description: "Learn the basics of candle creation and gain hands-on experience in a refined, high-standard learning setting.",
    intro: "This beginner course covers everything you need to start making candles — from understanding tools and materials to creating your first pillar, insect repellent, and floral candles. You'll also learn labeling and packaging to prepare your products for sale.",
    curriculum: [
      "Candle making basics",
      "Candle tools and materials",
      "Measuring and preparation",
      "Pillar candles",
      "Insect repellent candle",
      "Floral candle (Bouquet)",
      "Labeling and packaging",
    ],
    creations: [
      "Pillar Candles",
      "Insect Repellent Candles",
      "Floral / Bouquet Candles",
    ],
    includes: [
      "All tools and materials provided for in-class use",
      "Any candles made during the course can be taken home",
      "Certificate awarded upon successful completion",
    ],
  },
  {
    id: "advanced_candle",
    title: "Advanced Candle Making Course",
    price: 60000,
    kitPrice: 20000,
    duration: "Contact for schedule",
    schedule: "Monday – Friday: 10:30–12:30 | 2:30–4:30",
    icon: Flame,
    image: candleImage,
    tagline: "Level 2 — Master advanced candle techniques",
    description: "Take your candle making to the next level with advanced fragrance blending, luxury designs, and production scaling techniques.",
    intro: "This advanced course is designed for those who have completed the beginner level or have candle making experience. You'll master professional wick science, advanced fragrance blending, and luxury candle designs. The course also covers production and scaling to grow your candle business.",
    curriculum: [
      "Professional wick science",
      "Advanced fragrance blending",
      "Luxury candle design",
      "Massage candle",
      "Whipped candle",
      "Jelly candle",
      "Mason jar candle",
      "Production and scaling",
    ],
    creations: [
      "Massage Candles",
      "Whipped Candles",
      "Jelly Candles",
      "Mason Jar Candles",
      "Luxury Designer Candles",
    ],
    includes: [
      "All tools and materials provided for in-class use",
      "Any candles made during the course can be taken home",
      "Certificate awarded upon successful completion",
      "Production and scaling guidance for your business",
    ],
  },
  {
    id: "soap_making",
    title: "Soap Making Training Course",
    price: 60000,
    kitPrice: 20000,
    duration: "3 weeks (2 days per week)",
    schedule: "Monday – Friday: 10:30–12:30 | 2:30–4:30",
    icon: Droplets,
    image: soapImage,
    tagline: "Master the art of soap making from start to finish",
    description: "Master the art of soap making from start to finish in a refined, high-standard learning setting.",
    intro: "This comprehensive 3-week course is designed to equip you with the knowledge and practical skills needed to create high-quality, customized soaps. Students will be required to purchase a professional soap making kit, which includes all essential tools and materials needed for both training and continued practice. The kit allows students to keep their products and continue developing their soap-making skills after the course.",
    curriculum: [
      "Basic and essential tools",
      "Creating your own soap recipes",
      "Safety procedures, tools, and materials",
      "Formulating turmeric, kojic acid, acne, grape, oatmeal, and other specialty soaps",
      "Packaging and labeling techniques",
      "Understanding base oils and butters",
      "Scenting with essential oils",
      "Proper curing and storage methods",
    ],
    creations: null,
    includes: [
      "Professional soap making kit with all essential tools",
      "Students keep all soaps created during the course",
      "Certificate awarded upon successful completion",
      "Kit allows continued practice after the course",
    ],
  },
  {
    id: "body_butter_scrub",
    title: "Body Butter & Scrub Course",
    price: 60000,
    kitPrice: 15000,
    duration: "Contact for schedule",
    schedule: "Monday – Friday: 10:30–12:30 | 2:30–4:30",
    icon: Droplets,
    image: "https://images.pexels.com/photos/3737585/pexels-photo-3737585.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    tagline: "Learn to create luxurious body butters and scrubs",
    description: "Learn to create luxurious body butters and scrubs from scratch — perfect for personal use or starting your own skincare business.",
    intro: "This hands-on course covers everything from understanding skin types and ingredients to creating your own body butters and scrubs. You will learn about different butters, oils, and exfoliants, and how to formulate products for various skin needs. The course also covers business basics including pricing, packaging, and branding.",
    curriculum: [
      "Introduction: What is body butter & body scrub, skin types and benefits, hygiene and safety rules",
      "Ingredients: Butters (shea, cocoa), Oils (coconut, almond), Scrubs (sugar, salt, coffee), Essential oils and uses",
      "Skin lighting body scrub and butter",
      "Turmeric body scrub and butter",
      "Body scrub and butter for dry skin",
      "Whipped body butter and more",
      "Packaging and texture control",
      "How to whip body butter and create smooth texture",
      "Adding fragrance safely",
      "Business basics: Pricing products, packaging & branding",
    ],
    creations: [
      "Skin Lighting Scrub & Butter",
      "Turmeric Scrub & Butter",
      "Dry Skin Scrub & Butter",
      "Whipped Body Butter",
      "Custom Fragranced Products",
      "Packaged Ready-to-Sell Products",
    ],
    includes: [
      "All tools and materials provided for in-class use",
      "Students keep all products created during the course",
      "Certificate awarded upon successful completion",
      "Business basics: pricing, packaging & branding guidance",
    ],
  },
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const CourseDetail = ({ course, index }) => {
  const isEven = index % 2 === 0;

  return (
    <AnimatedSection>
      <div className="bento-card overflow-hidden" data-testid={`special-course-${course.id}`}>
        {/* Hero Image */}
        <div className="h-64 md:h-80 relative overflow-hidden">
          <img
            src={course.image}
            alt={course.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/40 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex items-end justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-[#121212]/80 border border-[#C5A059] flex items-center justify-center backdrop-blur-sm">
                    <course.icon className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('special-registration')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    data-testid="registration-open-label"
                    className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] hover:underline cursor-pointer bg-transparent border-none"
                  >
                    Registration Open
                  </button>
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-white">{course.title}</h3>
              </div>
              <div className="text-right">
                <span className="font-heading text-3xl text-[#C5A059]">${course.price.toLocaleString()}</span>
                <span className="font-body text-xs text-white block">GYD</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Quick Info Bar */}
          <div className="flex flex-wrap gap-4 mb-8 pb-6 border-b border-[#262626]">
            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <Clock size={14} className="text-[#C5A059]" />
              <span>{course.duration}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <Calendar size={14} className="text-[#C5A059]" />
              <span>{course.schedule}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <BookOpen size={14} className="text-[#C5A059]" />
              <span>Kit Fee: ${course.kitPrice.toLocaleString()} GYD</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
              <Award size={14} className="text-[#C5A059]" />
              <span>Certificate Included</span>
            </div>
          </div>

          {/* Description */}
          <p className="font-body text-[#A3A3A3] leading-relaxed mb-6">
            {course.intro}
          </p>

          <div className={`grid ${course.creations ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-8`}>
            {/* What You'll Learn */}
            <div>
              <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                What You Will Learn
              </h4>
              <ul className="space-y-3">
                {course.curriculum.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#A3A3A3] text-sm">
                    <ChevronRight size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Creations */}
            {course.creations && (
              <div>
                <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                  You Will Learn to Create
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {course.creations.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                      <CheckCircle size={14} className="text-[#C5A059] flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Includes */}
          <div className="mt-8 pt-6 border-t border-[#262626]">
            <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
              What's Included
            </h4>
            <div className="grid md:grid-cols-2 gap-3">
              {course.includes.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-[#A3A3A3] text-sm">
                  <CheckCircle size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Register / Interest Buttons */}
          <div className="mt-8 pt-6 border-t border-[#262626] flex gap-3">
            <button
              onClick={() => document.getElementById('special-registration')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid={`special-register-${course.id}`}
              className="flex-1 bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase py-3 text-center hover:brightness-110 transition-all"
            >
              Register Now
            </button>
            <Link
              to="/contact"
              data-testid={`special-interest-${course.id}`}
              className="flex-1 border border-[#C5A059] text-[#C5A059] font-semibold text-xs tracking-[0.15em] uppercase py-3 text-center hover:bg-[#C5A059]/10 transition-all"
            >
              Express Interest
            </Link>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

export const SpecialCoursesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="special-courses-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${candleImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Registration Now Open
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Special<br />
              <span className="gold-gradient">Courses</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
              Hands-on training in creative crafts. Learn new skills, earn your
              certificate, and start your own business. Limited spaces available — register now.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="special-courses-list">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-12">
          {specialCourses.map((course, index) => (
            <CourseDetail key={course.id} course={course} index={index} />
          ))}
        </div>
      </section>

      {/* Pricing Summary */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Limited Spaces
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Secure Your Spot
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-6">
                  Both courses are starting soon with limited spaces available.
                  Register early to secure your spot. Payment plans are available.
                </p>
                <div className="space-y-4">
                  {[
                    "Hands-on practical training",
                    "All materials provided for in-class use",
                    "Small class sizes for personalized attention",
                    "Certificate upon completion",
                    "Keep everything you create",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#C5A059]" />
                      <span className="font-body text-[#A3A3A3]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bento-card p-8">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                  Course Pricing (GYD)
                </h3>
                <div className="space-y-4">
                  {specialCourses.map((course) => (
                    <div key={course.id} className="flex justify-between items-center pb-4 border-b border-[#262626] last:border-0">
                      <div>
                        <span className="font-body text-white">{course.title}</span>
                        <span className="block text-xs text-[#A3A3A3]">+ Kit: ${course.kitPrice.toLocaleString()}</span>
                        <span className="block text-xs text-[#A3A3A3]">{course.duration}</span>
                      </div>
                      <span className="font-heading text-xl text-[#C5A059]">${course.price.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-2">
                  <Users size={14} className="text-[#C5A059]" />
                  <span className="text-xs text-[#A3A3A3]">Limited spaces — secure your spot early</span>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Registration Form */}
      <section id="special-registration" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="special-registration">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Register Today
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Ready to Learn?
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-8">
                  Select your course and submit your inquiry. We'll contact you
                  within 24 hours to discuss your goals and secure your spot.
                </p>
                <div className="bento-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Award className="w-6 h-6 text-[#C5A059]" />
                    <span className="font-body text-white">Certificate Awarded</span>
                  </div>
                  <p className="font-body text-sm text-[#A3A3A3]">
                    All courses include a certificate upon successful completion.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <InquiryForm inquiryType="special" defaultCourse="beginners_candle_special" />
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SpecialCoursesPage;
